import { v4 } from 'uuid';
import { Signal } from 'typed-signals';
import type { SeasonPassDependencies, SeasonPassInfo } from '../seasonPass/types';
import type { App } from '../../app/App';
import { ObservableValue } from '../../app/state/createObservableValue';
import { MetaPopupStackController } from '../../metaAbstractions/createMetaPopupController';
import { IAPPlacement, IAPProductId } from '../../app/iap/types';
import type { ResourceMap } from '../../app/resources/types';
import { TransactionReason } from '../../app/resources/types';
import { LIVES_MAX } from '../../app/state/lives/LivesConstants';

export function createSeasonPassDependencies(app: App): SeasonPassDependencies {
  const purchaseSignal = new Signal();
  app.iap?.onPurchaseSuccessful.connect((productId) => {
    if (productId === IAPProductId.SeasonPass as string) {
      purchaseSignal.emit();
    }
  });

  // TODO move to season pass config under extra data
  const seasonPassMaxLives = 8;

  app.utils.variableBank.setTextVariable('seasonPass.maxLives', `${seasonPassMaxLives}`);

  return {
    variableBank: app.utils.variableBank,
    onPremiumPassPurchaseMade: purchaseSignal,
    shareImage: app.platform.shareable
      ? async (imageBase64: Promise<string>): Promise<void> => {
          await app.platform.shareable?.share?.(await imageBase64);
        }
      : null,
    getPremiumPassPrice: async (): Promise<string | null> => {
      const product = await app.iap?.getProductAsync?.(IAPProductId.SeasonPass);
      if (!product) return null;
      return product.priceLabel;
    },
    observable: new ObservableValue(app.stateController, [
      'seasonPass',
      'levelIndex',
      'lastSeasonPass',
      'seasonPassFinishedAmount',
    ]),
    popupsController: new MetaPopupStackController(app.controller.popups),
    addResources(eventInfo: SeasonPassInfo, resources: ResourceMap): void {
      app.resources.makeTransaction(resources, TransactionReason.SeasonPassReward, {
        gameEventId: eventInfo.eventId,
      });
    },
    async purchaseSeasonPassProduct(eventInfo: SeasonPassInfo, impressionId?: string): Promise<boolean> {
      const product = await app?.iap?.getProductAsync?.(IAPProductId.SeasonPass);
      if (!product) return false;

      const success = await app.iap?.makePurchase({
        offerImpressionId: impressionId ?? v4(),
        placement: IAPPlacement.MainMenu,
        product,
        showResults: false,
      });

      app.tracking.trackSeasonPass({
        eventContext: 'Season Pass Purchase',
        points: eventInfo.tokens,
        pointsIncrease: 0,
        seasonPassId: eventInfo.eventId,
      });

      if (success) {
        app.stateController.setLivesMax(seasonPassMaxLives);
      }

      return success ?? false;
    },
    onSeasonEnd: (eventInfo: SeasonPassInfo): void => {
      app.stateController.setLivesMax(LIVES_MAX);
      app.tracking.trackSeasonPass({
        eventContext: 'Season Pass Complete',
        points: eventInfo.tokens,
        pointsIncrease: 0,
        seasonPassId: eventInfo.eventId,
      });
    },
    onSeasonStart: (eventInfo: SeasonPassInfo): void => {
      app.tracking.trackSeasonPass({
        eventContext: 'Season Pass Start',
        points: eventInfo.tokens,
        pointsIncrease: 0,
        seasonPassId: eventInfo.eventId,
      });
    },
    onProgressIncrease: (eventInfo: SeasonPassInfo, increase): void => {
      app.tracking.trackSeasonPass({
        eventContext: 'Points Update',
        points: eventInfo.tokens,
        pointsIncrease: increase,
        seasonPassId: eventInfo.eventId,
      });
    },
  };
}
